import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../apiServices/axios';
import {
    AI_WARDEN_DOCS_LIGHTWEIGHT,
    AI_WARDEN_FILTERS,
    AI_WARDEN_IRL_LIGHTWEIGHT,
    AI_WARDEN_OBS_LIGHTWEIGHT,
    INTELLIGENCE_ASK,
    INTELLIGENCE_CONVERSATION,
    INTELLIGENCE_CONVERSATION_MESSAGES,
    INTELLIGENCE_CONVERSATIONS,
    aiWardenWorkstreamQuery,
} from '../apiServices/api_routes';
import { enrichCitationsFromEvidence } from '../utils/citationDisplay';
import { normalizeObservationCandidates } from '../utils/intelligenceMeta';
import { normalizeResponseBlocks } from '../components/LensIntelligence/structured/structuredBlockUtils';
import {
    buildWorkstreamIdsQuery,
    countScopeIrlResponses,
    countScopeLightweightList,
} from '../utils/aiWardenScope';
import {
    createEmptyCustomContext,
    getCustomSourceWorkstreamIds,
    getUnionCustomWorkstreamIds,
    resolveDocumentsInScope,
    resolveIrlInScope,
    resolveObservationsInScope,
} from '../utils/lensIntelligenceCustomContext';
import { isIntelligenceSourceProcessing } from '../utils/intelligenceSourceReadiness';
import {
    serializeInsightsOriginForAsk,
    isInsightsModuleAskOrigin,
    readInsightsModuleAskContext,
} from '../utils/intelligenceInsightsAskLensOrigin';

/** When true, AiWarden Ask and IRL Find Evidence use the intelligence ask API. */
export const INTELLIGENCE_ASK_ENABLED = true;

function readyDocumentsOnly(files = []) {
    return (files || []).filter(f => !isIntelligenceSourceProcessing(f));
}

export function isIntelligenceAskEnabled() {
    return INTELLIGENCE_ASK_ENABLED;
}

export const ASK_CONTEXT_MODE = {
    ENTIRE_PROJECT: 'entire_project',
    SELECT_WORKSTREAMS: 'select_workstreams',
    CUSTOM: 'custom',
};

function parseIrlLightweightRows(data) {
    if (Array.isArray(data?.irls)) return data.irls;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
}

function parseObsLightweightRows(data) {
    if (Array.isArray(data?.observations)) return data.observations;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
}

/**
 * Map VDR scope + question to intelligence ask request body
 * (AskRequestSerializer: question, workstream_ids, folder_ids, file_ids, ws_id).
 */
export function buildIntelligenceAskBody(ragBody, scope, uiWsId, options = {}) {
    const out = { question: ragBody.question };
    if (scope.workstreamIds?.length) out.workstream_ids = scope.workstreamIds;
    if (scope.folderIds?.length) out.folder_ids = scope.folderIds;
    if (scope.fileIds?.length) out.file_ids = scope.fileIds;
    if (scope.vdrWorkstreamIds?.length) out.vdr_workstream_ids = scope.vdrWorkstreamIds;
    if (scope.irlWorkstreamIds?.length) out.irl_workstream_ids = scope.irlWorkstreamIds;
    if (scope.observationWorkstreamReprs?.length) {
        out.observation_workstream_reprs = scope.observationWorkstreamReprs;
    }
    if (scope.irlId != null) {
        const id = Number(scope.irlId);
        if (Number.isFinite(id)) out.primary_irl_id = id;
    }
    if (uiWsId != null && uiWsId !== '__ALL__') {
        const w = Number(uiWsId);
        if (Number.isFinite(w)) out.ws_id = w;
    }
    if (typeof options.includeVdr === 'boolean') {
        out.include_vdr = options.includeVdr;
    }
    if (typeof options.includeIrl === 'boolean') {
        out.include_irl = options.includeIrl;
    }
    if (typeof options.includeObservations === 'boolean') {
        out.include_observations = options.includeObservations;
    }
    if (options.irlTags?.length) {
        out.irl_tags = options.irlTags;
    }
    if (options.observationTags?.length) {
        out.observation_tags = options.observationTags;
    }
    if (options.accuracyMode === true) {
        out.accuracy_mode = true;
    }
    if (options.conversationId) {
        out.conversation_id = options.conversationId;
    }
    if (options.persistConversation === true) {
        out.persist_conversation = true;
    }
    if (options.insightsOrigin && isInsightsModuleAskOrigin(options.insightsOrigin)) {
        const hydrated = {
            ...options.insightsOrigin,
            moduleContext:
                readInsightsModuleAskContext(options.insightsOrigin) ||
                options.insightsOrigin.moduleContext ||
                null,
        };
        const origin = serializeInsightsOriginForAsk(hydrated);
        if (origin) {
            out.insights_origin = origin;
            out.do_not_regenerate_insights = true;
            out.retrieval_mode = 'insights_module';
            out.include_vdr = false;
            out.include_irl = false;
            out.include_observations = false;
        }
    }
    return out;
}

function normalizeCitationRef(ref) {
    if (ref == null || ref === '') return ref;
    if (typeof ref === 'number' && Number.isFinite(ref)) return `[${ref}]`;
    const s = String(ref).trim();
    if (/^\[\d+\]$/.test(s)) return s;
    if (/^\d+$/.test(s)) return `[${s}]`;
    return ref;
}

export function citationRefIndex(ref) {
    const m = String(ref ?? '')
        .trim()
        .match(/\[?(\d+)\]?/);
    return m ? Number(m[1]) : null;
}

/** Build rag_chat-style chunks[] from intelligence evidence + citations for AiWarden citation cards. */
export function evidenceToRagChunks(evidence = [], citations = []) {
    const byRef = new Map();
    for (const c of citations) {
        const idx = citationRefIndex(c.ref);
        if (idx != null) byRef.set(idx, c);
    }

    const evList = Array.isArray(evidence) ? evidence : [];
    if (evList.length > 0) {
        return evList.map((ev, index) => {
        const ref = index + 1;
        const cite = byRef.get(ref) || {};
        const meta = ev.metadata && typeof ev.metadata === 'object' ? ev.metadata : {};
        const raw = ev.raw && typeof ev.raw === 'object' ? ev.raw : {};
        const score = ev.retrieval_score ?? cite.evidence_score;
        const chunkId = meta.chunk_id || cite.chunk_id || ev.source_id;
        const fileId = meta.file_id ?? cite.file_id ?? cite.referenced_file_id;
        const docName = cite.document_name || meta.metadata_storage_name || meta.document_name;
        const sourceType = (ev.source_type || cite.source_type || meta.source_type || '').toString();

        const preview = ev.text_preview || cite.text_preview || '';
        return {
            chunk: preview || cite.chunk || '',
            chunk_id: chunkId,
            file_id: fileId,
            folder_id: meta.folder_id ?? cite.folder_id,
            workstream_id: meta.workstream_id ?? cite.workstream_id,
            metadata_storage_name: docName,
            document_name: docName,
            file_name: docName,
            chunk_doc_type: meta.chunk_doc_type,
            chunk_intent: meta.chunk_intent,
            chunk_topics: meta.chunk_topics,
            '@search.score': score,
            merged_chunk_index: index,
            source_type: sourceType,
            irl_ref: cite.irl_ref || meta.irl_ref,
            irl_id: cite.irl_id ?? meta.irl_id,
            observation_id: cite.observation_id ?? meta.observation_id,
            observation_ref: cite.observation_ref || meta.observation_ref,
            workstream_repr: cite.workstream_repr || meta.workstream_repr,
            tag_repr: cite.tag_repr || meta.tag_repr,
            assessment: cite.assessment || meta.assessment,
            swot: cite.swot || meta.swot,
            impact: cite.impact || meta.impact,
            observation_text: cite.observation_text || meta.observation_text || raw.observation_text,
            comments_text: cite.comments_text || meta.comments_text || raw.comments_text,
            comments: cite.comments || meta.comments || raw.comments,
            tag: cite.tag || meta.tag,
            workstream_name: cite.workstream_name || meta.workstream_name,
            request_excerpt: cite.request_excerpt || meta.request_excerpt,
            answer_excerpt: cite.answer_excerpt || meta.answer_excerpt,
            response_status: cite.response_status || meta.response_status || meta.status,
            has_response: cite.has_response ?? meta.has_response,
            intent_class: cite.intent_class || meta.intent_class,
            keywords: cite.keywords || meta.keywords,
            text_preview: preview || cite.text_preview,
        };
        });
    }

    const citeList = Array.isArray(citations) ? citations : [];
    return citeList.map((cite, index) => ({
        chunk: cite.text_preview || cite.chunk || cite.text || '',
        chunk_id: cite.chunk_id,
        file_id: cite.file_id ?? cite.referenced_file_id,
        folder_id: cite.folder_id,
        workstream_id: cite.workstream_id,
        metadata_storage_name: cite.document_name || cite.metadata_storage_name || cite.file_name,
        document_name: cite.document_name || cite.metadata_storage_name,
        file_name: cite.file_name || cite.document_name,
        '@search.score': cite.evidence_score,
        merged_chunk_index: Number.isInteger(cite.merged_chunk_index) ? cite.merged_chunk_index : index,
        source_type: cite.source_type,
        irl_ref: cite.irl_ref,
        irl_id: cite.irl_id,
        tag: cite.tag,
        workstream_name: cite.workstream_name,
        request_excerpt: cite.request_excerpt,
        answer_excerpt: cite.answer_excerpt,
        response_status: cite.response_status,
        has_response: cite.has_response,
        intent_class: cite.intent_class,
        role: cite.role,
    }));
}

/** Unwrap occasional nested API envelopes ({ data: { ... } }). */
export function unwrapIntelligencePayload(raw) {
    if (!raw || typeof raw !== 'object') return {};
    if (
        raw.question != null ||
        raw.answer != null ||
        Array.isArray(raw.citations) ||
        Array.isArray(raw.evidence)
    ) {
        return raw;
    }
    if (raw.data && typeof raw.data === 'object') {
        return unwrapIntelligencePayload(raw.data);
    }
    return raw;
}

/** Normalize intelligence ask API payload to AiWarden / PostRagChat response shape. */
export function normalizeIntelligenceAskResponse(raw) {
    const payload = unwrapIntelligencePayload(raw);
    const citationsBase = (Array.isArray(payload?.citations) ? payload.citations : []).map(c => {
        const ref = normalizeCitationRef(c.ref);
        const refIdx = citationRefIndex(ref);
        return {
            ...c,
            ref,
            merged_chunk_index: Number.isInteger(refIdx) ? refIdx - 1 : c.merged_chunk_index,
        };
    });
    const evidence = Array.isArray(payload?.evidence) ? payload.evidence : [];
    const citations = enrichCitationsFromEvidence(citationsBase, evidence);
    const chunks = evidenceToRagChunks(evidence, citations);

    let alignment_analysis = payload?.alignment_analysis ?? null;
    if (!alignment_analysis && payload?.retrieval_debug?.alignment_status) {
        alignment_analysis = {
            alignment_status: payload.retrieval_debug.alignment_status,
            confidence: payload.retrieval_debug.alignment_confidence,
            gaps: [],
            recommended_follow_up_questions: [],
        };
    }

    return {
        question: payload?.question,
        final_answer: payload?.answer ?? '',
        answer: payload?.answer ?? '',
        citations,
        chunks,
        confidence: payload?.confidence,
        trace_id: payload?.trace_id ?? null,
        conversation_id: payload?.conversation_id ?? null,
        retrieval_debug: payload?.retrieval_debug || {},
        evidence_governance: payload?.evidence_governance ?? null,
        alignment_analysis,
        irl_claim_validation: payload?.irl_claim_validation ?? null,
        observation_candidates: normalizeObservationCandidates(payload?.observation_candidates),
        response_blocks: normalizeResponseBlocks(payload?.response_blocks ?? payload?.blocks),
        suggested_actions: Array.isArray(payload?.suggested_actions)
            ? payload.suggested_actions
            : null,
        response_actions: payload?.response_actions ?? null,
        evidence,
        intelligence_source: true,
    };
}

export const postIntelligenceAsk = createAsyncThunk(
    'intelligence/postAsk',
    async ({ projectId, body }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(INTELLIGENCE_ASK(projectId), body);
            return normalizeIntelligenceAskResponse(response.data);
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(typeof d === 'object' ? d : { detail: error.message || 'Intelligence ask failed' });
        }
    },
);

export const fetchIntelligenceConversations = createAsyncThunk(
    'intelligence/fetchConversations',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(INTELLIGENCE_CONVERSATIONS(projectId));
            return {
                projectId: String(projectId),
                items: response.data?.items || [],
            };
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(typeof d === 'object' ? d : { detail: error.message || 'Failed to load chats' });
        }
    },
);

export const createIntelligenceConversation = createAsyncThunk(
    'intelligence/createConversation',
    async ({ projectId, title = '', scope_json = {} }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(INTELLIGENCE_CONVERSATIONS(projectId), {
                title,
                scope_json,
            });
            return response.data;
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(typeof d === 'object' ? d : { detail: error.message || 'Failed to create chat' });
        }
    },
);

export const deleteIntelligenceConversation = createAsyncThunk(
    'intelligence/deleteConversation',
    async ({ projectId, conversationId }, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(INTELLIGENCE_CONVERSATION(projectId, conversationId));
            return conversationId;
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(typeof d === 'object' ? d : { detail: error.message || 'Failed to delete chat' });
        }
    },
);

export const fetchIntelligenceMessages = createAsyncThunk(
    'intelligence/fetchMessages',
    async ({ projectId, conversationId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(
                INTELLIGENCE_CONVERSATION_MESSAGES(projectId, conversationId),
            );
            return {
                projectId: String(projectId),
                conversationId,
                messages: response.data?.messages || [],
                totalCount: response.data?.total_count ?? 0,
            };
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(typeof d === 'object' ? d : { detail: error.message || 'Failed to load messages' });
        }
    },
);

export const postIntelligenceContextChange = createAsyncThunk(
    'intelligence/postContextChange',
    async ({ projectId, conversationId, label, scope_json = {} }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                INTELLIGENCE_CONVERSATION_MESSAGES(projectId, conversationId),
                { label, scope_json },
            );
            return {
                conversationId,
                message: response.data,
            };
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(
                typeof d === 'object' ? d : { detail: error.message || 'Failed to record context change' },
            );
        }
    },
);

/** Full-project VDR/IRL/Observations catalog for Choose context + readiness. */
export const fetchIntelligenceScopeCatalog = createAsyncThunk(
    'intelligence/fetchScopeCatalog',
    async ({ projectId }, { rejectWithValue }) => {
        try {
            const q = aiWardenWorkstreamQuery(null);
            const [docsRes, irlRes, obsRes] = await Promise.all([
                axiosInstance.get(`${AI_WARDEN_DOCS_LIGHTWEIGHT(projectId)}${q}`),
                axiosInstance.get(`${AI_WARDEN_IRL_LIGHTWEIGHT(projectId)}${q}`),
                axiosInstance.get(`${AI_WARDEN_OBS_LIGHTWEIGHT(projectId)}${q}`),
            ]);
            return {
                projectId: String(projectId),
                documents: Array.isArray(docsRes.data?.documents) ? docsRes.data.documents : [],
                irls: parseIrlLightweightRows(irlRes.data),
                observations: parseObsLightweightRows(obsRes.data),
            };
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(
                typeof d === 'object' ? d : { detail: error.message || 'Failed to load sources' },
            );
        }
    },
);

/**
 * Scoped readiness stats for the active Ask AI context mode.
 * Prior stats stay in the slice until this fulfills (no zero flash).
 */
export const fetchIntelligenceScopeStats = createAsyncThunk(
    'intelligence/fetchScopeStats',
    async (
        {
            projectId,
            contextMode,
            selectedWorkstreams = [],
            customContext = null,
            allWorkstreamIds = [],
        },
        { rejectWithValue },
    ) => {
        try {
            const pid = String(projectId);

            if (contextMode === ASK_CONTEXT_MODE.CUSTOM) {
                const union = getUnionCustomWorkstreamIds(customContext, allWorkstreamIds);
                if (!union.length) {
                    return {
                        projectId: pid,
                        stats: { documents: 0, irlResponses: 0, observations: 0 },
                        modalFiles: [],
                    };
                }

                const vdrIds = getCustomSourceWorkstreamIds(
                    customContext,
                    'documents',
                    allWorkstreamIds,
                )
                    .map(Number)
                    .filter(Number.isFinite);
                const irlIds = getCustomSourceWorkstreamIds(customContext, 'irl', allWorkstreamIds)
                    .map(Number)
                    .filter(Number.isFinite);
                const obsIds = getCustomSourceWorkstreamIds(
                    customContext,
                    'observations',
                    allWorkstreamIds,
                )
                    .map(Number)
                    .filter(Number.isFinite);

                const requests = [];
                if (customContext?.documents?.enabled && vdrIds.length) {
                    const q = aiWardenWorkstreamQuery(vdrIds);
                    requests.push(
                        axiosInstance.get(`${AI_WARDEN_FILTERS(projectId)}${q}`),
                        axiosInstance.get(`${AI_WARDEN_DOCS_LIGHTWEIGHT(projectId)}${q}`),
                    );
                }
                if (customContext?.irl?.enabled && irlIds.length) {
                    requests.push(
                        axiosInstance.get(
                            `${AI_WARDEN_IRL_LIGHTWEIGHT(projectId)}${aiWardenWorkstreamQuery(irlIds)}`,
                        ),
                    );
                }
                if (customContext?.observations?.enabled && obsIds.length) {
                    requests.push(
                        axiosInstance.get(
                            `${AI_WARDEN_OBS_LIGHTWEIGHT(projectId)}${aiWardenWorkstreamQuery(obsIds)}`,
                        ),
                    );
                }

                const results = await Promise.all(requests);
                let docCount = 0;
                let irlCount = 0;
                let obsCount = 0;
                let files = [];
                let idx = 0;

                if (customContext?.documents?.enabled && vdrIds.length) {
                    const folders = results[idx]?.data?.folders || [];
                    const docs = Array.isArray(results[idx + 1]?.data?.documents)
                        ? results[idx + 1].data.documents
                        : [];
                    idx += 2;
                    files = readyDocumentsOnly(
                        resolveDocumentsInScope(
                            customContext,
                            docs,
                            folders,
                            allWorkstreamIds,
                        ),
                    );
                    docCount = files.length;
                }
                if (customContext?.irl?.enabled && irlIds.length) {
                    const irlRows = parseIrlLightweightRows(results[idx]?.data);
                    idx += 1;
                    irlCount = resolveIrlInScope(customContext, irlRows, allWorkstreamIds).length;
                }
                if (customContext?.observations?.enabled && obsIds.length) {
                    const obsRows = parseObsLightweightRows(results[idx]?.data);
                    obsCount = resolveObservationsInScope(customContext, obsRows).length;
                }

                return {
                    projectId: pid,
                    stats: {
                        documents: docCount,
                        irlResponses: irlCount,
                        observations: obsCount,
                    },
                    modalFiles: files,
                };
            }

            const wsQueryIds = buildWorkstreamIdsQuery(selectedWorkstreams, allWorkstreamIds);
            const q = aiWardenWorkstreamQuery(wsQueryIds);
            const [docsRes, irlRes, obsRes] = await Promise.all([
                axiosInstance.get(`${AI_WARDEN_DOCS_LIGHTWEIGHT(projectId)}${q}`),
                axiosInstance.get(`${AI_WARDEN_IRL_LIGHTWEIGHT(projectId)}${q}`),
                axiosInstance.get(`${AI_WARDEN_OBS_LIGHTWEIGHT(projectId)}${q}`),
            ]);
            const files = readyDocumentsOnly(
                Array.isArray(docsRes.data?.documents) ? docsRes.data.documents : [],
            );
            return {
                projectId: pid,
                stats: {
                    documents: files.length,
                    irlResponses: countScopeIrlResponses(irlRes.data),
                    observations: countScopeLightweightList(obsRes.data, [
                        'observations',
                        'items',
                        'results',
                    ]),
                },
                modalFiles: files,
            };
        } catch (error) {
            const d = error.response?.data;
            return rejectWithValue(
                typeof d === 'object' ? d : { detail: error.message || 'Failed to load scope stats' },
            );
        }
    },
);

const emptyScopeStats = () => ({
    documents: 0,
    irlResponses: 0,
    observations: 0,
});

const emptyScopeCatalog = () => ({
    documents: [],
    irls: [],
    observations: [],
});

const initialState = {
    lastAsk: null,
    lastAskError: null,
    conversations: [],
    conversationsLoading: false,
    conversationsError: null,
    activeConversationId: null,
    messages: [],
    messagesLoading: false,
    messagesError: null,
    askContextProjectId: null,
    askContextMode: ASK_CONTEXT_MODE.ENTIRE_PROJECT,
    askSelectedWorkstreams: [],
    askCustomContext: createEmptyCustomContext(),
    scopeCatalogProjectId: null,
    scopeCatalog: emptyScopeCatalog(),
    scopeCatalogLoading: false,
    scopeStatsProjectId: null,
    scopeStats: emptyScopeStats(),
    scopeModalFiles: [],
    scopeLoading: false,
    insightsAskOrigin: null,
    insightsAskOriginByConversationId: {},
};

const intelligenceSlice = createSlice({
    name: 'intelligence',
    initialState,
    reducers: {
        setActiveConversationId(state, action) {
            state.activeConversationId = action.payload || null;
            if (!action.payload) {
                state.messages = [];
            }
        },
        clearAskAiChat(state) {
            state.activeConversationId = null;
            state.messages = [];
            state.messagesError = null;
            state.lastAsk = null;
            state.lastAskError = null;
            state.insightsAskOrigin = null;
        },
        setInsightsAskOrigin(state, action) {
            state.insightsAskOrigin = action.payload || null;
        },
        bindInsightsAskOriginConversation(state, action) {
            const conversationId = action.payload;
            if (!conversationId || !state.insightsAskOrigin) return;
            const next = { ...state.insightsAskOrigin, conversationId };
            state.insightsAskOrigin = next;
            if (!state.insightsAskOriginByConversationId) {
                state.insightsAskOriginByConversationId = {};
            }
            state.insightsAskOriginByConversationId[conversationId] = next;
        },
        upsertConversationLocal(state, action) {
            const conv = action.payload;
            if (!conv?.id) return;
            if (!Array.isArray(state.conversations)) state.conversations = [];
            const idx = state.conversations.findIndex(c => c.id === conv.id);
            if (idx >= 0) {
                state.conversations[idx] = { ...state.conversations[idx], ...conv };
            } else {
                state.conversations.unshift(conv);
            }
            state.conversations.sort(
                (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0),
            );
        },
        appendOptimisticUserMessage(state, action) {
            const content = String(action.payload?.content || '').trim();
            if (!content) return;
            if (!Array.isArray(state.messages)) state.messages = [];
            const tempId = action.payload?.id || `optimistic-user-${Date.now()}`;
            // Avoid duplicating if the same optimistic send is retried.
            if (state.messages.some(m => m.id === tempId)) return;
            state.messages.push({
                id: tempId,
                role: 'user',
                content,
                created_at: action.payload?.created_at || new Date().toISOString(),
                optimistic: true,
            });
        },
        appendLocalSystemMessage(state, action) {
            const content = String(action.payload?.content || '').trim();
            if (!content) return;
            if (!Array.isArray(state.messages)) state.messages = [];
            const tempId = action.payload?.id || `system-${Date.now()}`;
            if (state.messages.some(m => m.id === tempId)) return;
            state.messages.push({
                id: tempId,
                role: 'system',
                content,
                meta: action.payload?.meta || { type: 'context_change' },
                created_at: action.payload?.created_at || new Date().toISOString(),
                optimistic: true,
            });
        },
        appendLocalAssistantTurn(state, action) {
            const {
                question = '',
                content = '',
                citations = [],
                evidence = [],
                confidence = '',
                meta = {},
                conversationId = null,
            } = action.payload || {};
            if (!Array.isArray(state.messages)) state.messages = [];
            const trimmedQuestion = String(question || '').trim();
            if (trimmedQuestion) {
                const userIdx = state.messages.findIndex(
                    m => m.optimistic && m.role === 'user' && m.content === trimmedQuestion,
                );
                if (userIdx >= 0) {
                    state.messages[userIdx] = {
                        ...state.messages[userIdx],
                        optimistic: false,
                    };
                }
            }
            const assistantId = action.payload?.id || `local-assistant-${Date.now()}`;
            if (state.messages.some(m => m.id === assistantId)) return;
            state.messages.push({
                id: assistantId,
                role: 'assistant',
                content: String(content || ''),
                citations: Array.isArray(citations) ? citations : [],
                evidence: Array.isArray(evidence) ? evidence : [],
                confidence: confidence || '',
                meta: meta && typeof meta === 'object' ? meta : {},
                created_at: action.payload?.created_at || new Date().toISOString(),
                optimistic: true,
            });
            if (conversationId) {
                state.activeConversationId = conversationId;
            }
        },
        ensureAskContextProject(state, action) {
            const projectId = action.payload != null ? String(action.payload) : null;
            if (!projectId) return;
            if (state.askContextProjectId === projectId) return;
            state.askContextProjectId = projectId;
            state.askContextMode = ASK_CONTEXT_MODE.ENTIRE_PROJECT;
            state.askSelectedWorkstreams = [];
            state.askCustomContext = createEmptyCustomContext();
            // Drop prior project's Ask Lens thread / list so UI cannot show cross-project chat.
            state.activeConversationId = null;
            state.messages = [];
            state.messagesLoading = false;
            state.messagesError = null;
            state.conversations = [];
            state.conversationsLoading = false;
            state.conversationsError = null;
            state.lastAsk = null;
            state.lastAskError = null;
            if (state.scopeCatalogProjectId !== projectId) {
                state.scopeCatalogProjectId = null;
                state.scopeCatalog = emptyScopeCatalog();
            }
            if (state.scopeStatsProjectId !== projectId) {
                state.scopeStatsProjectId = null;
                state.scopeStats = emptyScopeStats();
                state.scopeModalFiles = [];
            }
        },
        setAskContextMode(state, action) {
            state.askContextMode = action.payload || ASK_CONTEXT_MODE.ENTIRE_PROJECT;
        },
        setAskSelectedWorkstreams(state, action) {
            state.askSelectedWorkstreams = Array.isArray(action.payload) ? action.payload : [];
        },
        setAskCustomContext(state, action) {
            state.askCustomContext = action.payload || createEmptyCustomContext();
        },
        applyAskContext(state, action) {
            const { mode, selectedWorkstreams, customContext, projectId } = action.payload || {};
            if (projectId != null) state.askContextProjectId = String(projectId);
            if (mode) state.askContextMode = mode;
            if (selectedWorkstreams) state.askSelectedWorkstreams = selectedWorkstreams;
            if (customContext) state.askCustomContext = customContext;
        },
        clearIntelligenceScopeCache(state) {
            state.scopeCatalogProjectId = null;
            state.scopeCatalog = emptyScopeCatalog();
            state.scopeCatalogLoading = false;
            state.scopeStatsProjectId = null;
            state.scopeStats = emptyScopeStats();
            state.scopeModalFiles = [];
            state.scopeLoading = false;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(postIntelligenceAsk.fulfilled, (state, action) => {
                state.lastAsk = action.payload;
                state.lastAskError = null;
                if (action.payload?.conversation_id) {
                    state.activeConversationId = action.payload.conversation_id;
                }
            })
            .addCase(postIntelligenceAsk.rejected, (state, action) => {
                state.lastAskError = action.payload || action.error;
            })
            .addCase(fetchIntelligenceConversations.pending, state => {
                state.conversationsLoading = true;
                state.conversationsError = null;
            })
            .addCase(fetchIntelligenceConversations.fulfilled, (state, action) => {
                const payloadProjectId = action.payload?.projectId;
                if (
                    payloadProjectId != null &&
                    state.askContextProjectId != null &&
                    String(payloadProjectId) !== String(state.askContextProjectId)
                ) {
                    return;
                }
                state.conversationsLoading = false;
                state.conversations = action.payload?.items || [];
            })
            .addCase(fetchIntelligenceConversations.rejected, (state, action) => {
                state.conversationsLoading = false;
                state.conversationsError = action.payload || action.error;
            })
            .addCase(createIntelligenceConversation.fulfilled, (state, action) => {
                if (action.payload?.id) {
                    state.conversations.unshift(action.payload);
                    state.activeConversationId = action.payload.id;
                    state.messages = [];
                }
            })
            .addCase(deleteIntelligenceConversation.fulfilled, (state, action) => {
                const id = action.payload;
                state.conversations = state.conversations.filter(c => c.id !== id);
                if (state.activeConversationId === id) {
                    state.activeConversationId = null;
                    state.messages = [];
                }
            })
            .addCase(fetchIntelligenceMessages.pending, state => {
                state.messagesLoading = true;
                state.messagesError = null;
            })
            .addCase(fetchIntelligenceMessages.fulfilled, (state, action) => {
                const payloadProjectId = action.payload?.projectId;
                if (
                    payloadProjectId != null &&
                    state.askContextProjectId != null &&
                    String(payloadProjectId) !== String(state.askContextProjectId)
                ) {
                    return;
                }
                state.messagesLoading = false;
                state.activeConversationId = action.payload.conversationId;
                state.messages = action.payload.messages || [];
            })
            .addCase(fetchIntelligenceMessages.rejected, (state, action) => {
                state.messagesLoading = false;
                state.messagesError = action.payload || action.error;
            })
            .addCase(postIntelligenceContextChange.fulfilled, (state, action) => {
                const msg = action.payload?.message;
                if (!msg?.id) return;
                if (!Array.isArray(state.messages)) state.messages = [];
                // Replace matching optimistic system marker
                const optimisticIdx = state.messages.findIndex(
                    m => m.optimistic && m.role === 'system' && m.content === msg.content,
                );
                if (optimisticIdx >= 0) {
                    state.messages[optimisticIdx] = msg;
                } else if (!state.messages.some(m => m.id === msg.id)) {
                    state.messages.push(msg);
                }
            })
            .addCase(fetchIntelligenceScopeCatalog.pending, state => {
                state.scopeCatalogLoading = true;
            })
            .addCase(fetchIntelligenceScopeCatalog.fulfilled, (state, action) => {
                state.scopeCatalogLoading = false;
                state.scopeCatalogProjectId = action.payload.projectId;
                state.scopeCatalog = {
                    documents: action.payload.documents || [],
                    irls: action.payload.irls || [],
                    observations: action.payload.observations || [],
                };
            })
            .addCase(fetchIntelligenceScopeCatalog.rejected, state => {
                state.scopeCatalogLoading = false;
            })
            .addCase(fetchIntelligenceScopeStats.pending, state => {
                state.scopeLoading = true;
            })
            .addCase(fetchIntelligenceScopeStats.fulfilled, (state, action) => {
                state.scopeLoading = false;
                state.scopeStatsProjectId = action.payload.projectId;
                state.scopeStats = action.payload.stats || emptyScopeStats();
                state.scopeModalFiles = Array.isArray(action.payload.modalFiles)
                    ? action.payload.modalFiles
                    : [];
            })
            .addCase(fetchIntelligenceScopeStats.rejected, state => {
                state.scopeLoading = false;
            });
    },
});

export const {
    setActiveConversationId,
    clearAskAiChat,
    setInsightsAskOrigin,
    bindInsightsAskOriginConversation,
    upsertConversationLocal,
    appendOptimisticUserMessage,
    appendLocalSystemMessage,
    appendLocalAssistantTurn,
    ensureAskContextProject,
    setAskContextMode,
    setAskSelectedWorkstreams,
    setAskCustomContext,
    applyAskContext,
    clearIntelligenceScopeCache,
} = intelligenceSlice.actions;

/** Merge defaults so redux-persist rehydration of older slice shapes stays safe. */
export default function intelligenceReducer(state, action) {
    const next = intelligenceSlice.reducer(state, action);
    return {
        ...initialState,
        ...next,
        conversations: Array.isArray(next?.conversations) ? next.conversations : [],
        messages: Array.isArray(next?.messages) ? next.messages : [],
        askSelectedWorkstreams: Array.isArray(next?.askSelectedWorkstreams)
            ? next.askSelectedWorkstreams
            : [],
        askCustomContext: next?.askCustomContext || createEmptyCustomContext(),
        scopeCatalog: {
            ...emptyScopeCatalog(),
            ...(next?.scopeCatalog || {}),
            documents: Array.isArray(next?.scopeCatalog?.documents)
                ? next.scopeCatalog.documents
                : [],
            irls: Array.isArray(next?.scopeCatalog?.irls) ? next.scopeCatalog.irls : [],
            observations: Array.isArray(next?.scopeCatalog?.observations)
                ? next.scopeCatalog.observations
                : [],
        },
        scopeStats: {
            ...emptyScopeStats(),
            ...(next?.scopeStats || {}),
        },
        scopeModalFiles: Array.isArray(next?.scopeModalFiles) ? next.scopeModalFiles : [],
        insightsAskOrigin: next?.insightsAskOrigin || null,
        insightsAskOriginByConversationId:
            next?.insightsAskOriginByConversationId &&
            typeof next.insightsAskOriginByConversationId === 'object'
                ? next.insightsAskOriginByConversationId
                : {},
    };
}
